import React, { useContext } from 'react';
import styled from 'styled-components';

import CardContext from 'contexts/CardContext';
import DashboardContextMenuContext from 'contexts/DashboardContextMenuContext';
import DashboardGadgetContext from 'contexts/DashboardGadgetContext';
import useExplainValue from 'components/InsightsChat/explainValue/useExplainValue';
import useGadgetDrillTarget from 'components/InsightsChat/explainValue/useGadgetDrillTarget';
import { useCompositeChartGadget } from 'components/Studio/CompositeCard/compositeChartPopup';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

import Option from './Option';

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Divider = styled.div`
  height: 1px;
  margin: 2px 0;
  background-color: var(--color-border-secondary);
`;

// The first row of the dashboard chart click menu: hand the clicked value to the
// Insights chat instead of opening a popup grid for it.
//
// Self-gating. The menu container already sits under CardContext (the card id
// the values registry is keyed by), DashboardGadgetContext (the gadget's chart
// def and popup link) and the dashboard's InsightsViewScopeContext (null on
// wallboards, where this renders nothing), so nothing about the menu's own
// context or the chart option files has to change to add this row.
const ExplainValueOption = ({ label }: { label: string }) => {
  const { isAvailable, explain } = useExplainValue();
  const { drillDown, metricIds, closeMenu } = useContext(
    DashboardContextMenuContext,
  );
  const { i: cardId, chartDefinition } = useContext(CardContext);
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  // A Composite's chart has no gadget, but may have a popup of its own
  const compositeChartGadget = useCompositeChartGadget();
  const drillTarget = useGadgetDrillTarget(
    dashboardGadget ?? compositeChartGadget,
  );

  if (!isAvailable || label === '' || !drillDown) {
    return null;
  }

  // A Composite's chart has no gadget; its card context holds the chart
  const chartDef = dashboardGadget?.chartDef ?? chartDefinition;

  return (
    <React.Fragment>
      <Option
        data-testid="explain-value-option"
        onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          e.preventDefault();
          e.stopPropagation();
          explain({
            drillDown,
            metricIds,
            chartDef,
            cardId,
            gadgetTitle: chartDef?.name ?? '',
            drillTarget,
            source: 'menu',
          });
          closeMenu();
        }}
      >
        <Label>
          <Icon
            icon="sparkles"
            width={14}
            height={14}
            color="var(--color-ai-accent)"
          />
          <Typography.Body type={'Label'}>Explain this value</Typography.Body>
        </Label>
      </Option>
      <Divider />
    </React.Fragment>
  );
};

export default ExplainValueOption;
